import { useMemo, useState, useCallback, memo, useEffect, useRef } from "react";

import { type DropdownOption } from "@react/components/Dropdown";
import type { IconName } from "@react/components/Icon";
import { COUNTRY_FLAG_ICONS } from "@root/tablescape/constants";
import {
  AVAILABLE_MARKETS,
  type MarketIsoCode,
  type MarketLanguage,
} from "@root/tablescape/constants";
import { useGeolocation } from "@root/tablescape/react/hooks/useGeolocation";
import { ObjectKeys } from "@root/tablescape/utilities";
import {
  getShopifyCountry,
  isMarketIsoCode,
} from "@root/tablescape/utilities/markets";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";

import { CountrySelectorModalRootView } from "./CountrySelectorModalRoot.view";

type CountryDropdownOption = {
  iconName?: IconName;
  name: string;
  value: MarketIsoCode;
};

const CountrySelectorModalRootBase = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const userClosedRef = useRef<boolean>(false);
  const { text } = useSectionSettings<{
    text: {
      closeAlt: string;
      continue: string;
      countryLabel: string;
      languageLabel: string;
      subtitle: string;
      title: string;
    };
  }>();

  const countries = useMemo(() => ObjectKeys(AVAILABLE_MARKETS), []);

  const countryOptions = useMemo<CountryDropdownOption[]>(
    () =>
      countries.map((isoKey) => {
        const iso = String(isoKey);
        if (!isMarketIsoCode(iso)) {
          return { name: "", value: "US" } as CountryDropdownOption; // unreachable
        }
        return {
          iconName: COUNTRY_FLAG_ICONS[iso],
          name: AVAILABLE_MARKETS[iso].country,
          value: iso,
        };
      }),
    [countries]
  );

  const [selectedCountry, setSelectedCountry] = useState<CountryDropdownOption>(
    () => {
      const shopifyCountry = getShopifyCountry();
      const match = countryOptions.find((opt) => opt.value === shopifyCountry);
      return match ?? countryOptions[0];
    }
  );

  const languagesForCountry = useMemo<readonly MarketLanguage[]>(() => {
    const iso = selectedCountry.value;
    return AVAILABLE_MARKETS[iso].languages;
  }, [selectedCountry.value]);

  const languageOptions = useMemo<DropdownOption[]>(() => {
    return languagesForCountry.map((lang) => ({
      name: lang.name,
      value: lang.value,
    }));
  }, [languagesForCountry]);

  const [selectedLanguage, setSelectedLanguage] = useState<DropdownOption>(
    () => {
      const iso = getShopifyCountry() ?? selectedCountry.value;
      const firstLang = AVAILABLE_MARKETS[iso].languages[0];
      return { name: firstLang.name, value: firstLang.value };
    }
  );

  const onSelectCountry = useCallback(
    (opt: DropdownOption) => {
      const value = opt.value;
      const iso = isMarketIsoCode(value) ? value : selectedCountry.value;
      setSelectedCountry({
        name: AVAILABLE_MARKETS[iso].country,
        value: iso,
      });
      const firstLang = AVAILABLE_MARKETS[iso].languages[0];
      setSelectedLanguage({ name: firstLang.name, value: firstLang.value });
    },
    [selectedCountry.value]
  );

  const onSelectLanguage = useCallback(
    (opt: DropdownOption) => setSelectedLanguage(opt),
    []
  );

  const modalDismissed = useMemo(
    () => window.localStorage.getItem("locationManuallySet"),
    []
  );

  const onClose = useCallback(() => {
    userClosedRef.current = true;

    const country = getShopifyCountry();
    if (country) {
      window.localStorage.setItem("locationManuallySet", country);
    }

    setIsOpen(false);
  }, []);

  const onContinue = useCallback(() => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          "locationManuallySet",
          String(selectedCountry.value)
        );
        window.maestro?.fireTrackEvent?.("clickCountrySelectionModal", {
          URL: window.location.href,
          path: window.location.pathname,
          "click info": "continue",
        });
        const market = AVAILABLE_MARKETS[selectedCountry.value];
        const targetLang =
          market.languages.find((l) => l.value === selectedLanguage.value) ||
          market.languages[0];
        const url = `https://${targetLang.domain}`;
        window.location.assign(url);
      }
    } finally {
      setIsOpen(false);
    }
  }, [selectedCountry.value, selectedLanguage.value]);

  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const forceShow = ["true", "1"].includes(
    params?.get("modal:geolocation") ?? ""
  );
  const { customerLocationMatchesStore, isLoading } = useGeolocation();

  useEffect(() => {
    if (userClosedRef.current) {
      return;
    }
    if (forceShow) {
      setIsOpen(true);
      return;
    }

    if (modalDismissed) {
      setIsOpen(false);
      return;
    }

    if (!isLoading) {
      setIsOpen(!customerLocationMatchesStore);
    }
  }, [forceShow, modalDismissed, customerLocationMatchesStore, isLoading]);

  return (
    <CountrySelectorModalRootView
      countryOptions={countryOptions}
      initialCountry={selectedCountry}
      initialLanguage={selectedLanguage}
      isOpen={isOpen}
      languageOptions={languageOptions}
      onClose={onClose}
      onContinue={onContinue}
      onSelectCountry={onSelectCountry}
      onSelectLanguage={onSelectLanguage}
      text={text}
    />
  );
};

export const CountrySelectorModalRoot = memo(CountrySelectorModalRootBase);
